// src/features/global-search/api.ts
//
// Typed client for the platform-wide search endpoint
// (GET /api/v1/global-search) that backs <GlobalSearchBar />.
//
// The TypeScript contract below mirrors the backend Pydantic models in
// backend/app/domain/global_search_screener/schemas.py
// (SearchResultDTO / SearchResponse) — keep the two in sync when the API
// changes.
//
// AUTH: we deliberately go through the shared axios `apiClient` (not a
// raw fetch) so the request automatically carries the Forge Ultra JWT
// (Authorization: Bearer, sourced from the `forge_token` localStorage key
// by the request interceptor) AND the `forge_session` cookie, and so a
// 401 flows through the central silent-refresh / bounce-to-WP handling in
// api/client.ts — exactly like every other authenticated call in the SPA.

import { apiClient } from '../../api/client';

// Routing discriminant — matches AssetClass in the backend enum. Drives
// which deep-dive page <GlobalSearchBar /> navigates to on selection and
// which colour badge is rendered.
export type AssetClass = 'equity' | 'etf' | 'reit' | 'crypto';

// One search hit. `market_cap` is USD; null/0 for the breadth-layer
// symbols the sync hasn't enriched yet (see the sync_task docstring).
export interface SearchResult {
    symbol: string;
    name: string;
    asset_class: AssetClass;
    exchange?: string | null;
    sector?: string | null;
    market_cap?: number | null;
}

export interface SearchResponse {
    query: string;
    results: SearchResult[];
    execution_time_ms: number;
}

/**
 * Query the global search index. Fails SILENTLY (returns `[]`) on any
 * non-auth error so the autocomplete UI degrades to "no results" rather
 * than throwing. A 401 is NOT swallowed here — it propagates into the
 * apiClient interceptor which owns the silent-refresh / re-login flow.
 *
 * @param query Raw user input (trimmed here; empty -> no request).
 * @param limit Max results (backend caps at 50; default 8 for the palette).
 */
export async function fetchGlobalSearch(
    query: string,
    limit = 8,
): Promise<SearchResult[]> {
    const q = query.trim();
    if (q.length === 0) return [];

    try {
        const { data } = await apiClient.get<SearchResponse>('/global-search', {
            params: { q, limit },
        });
        return data.results ?? [];
    } catch (err) {
        // eslint-disable-next-line no-console
        console.error('[globalSearch] request failed', err);
        return [];
    }
}
